import { FC, ReactNode, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'next-i18next'

import { useCustomIntercom } from 'hooks/common/intercom'

import type { Space } from 'features/space/types/spaceDetail'

import space_styles from './StorageInformationSection.module.scss'

type Props = {
  space: Space
  /** 最大預け入れ数の表示行 (space_type ごとに呼び出し側で組み立てる) */
  luggageLines: string[]
  showReportButton?: boolean
  children?: ReactNode
}

const DAY_TRANSLATION_KEYS = [
  'COMMON.DAY.SUNDAY',
  'COMMON.DAY.MONDAY',
  'COMMON.DAY.TUESDAY',
  'COMMON.DAY.WEDNESDAY',
  'COMMON.DAY.THURSDAY',
  'COMMON.DAY.FRIDAY',
  'COMMON.DAY.SATURDAY',
]

/** 保管情報セクション (営業時間アコーディオン + 最大預け入れ数)。 */
export const StorageInformationSection: FC<Props> = ({
  space,
  luggageLines,
  showReportButton = false,
  children,
}) => {
  const { t, i18n } = useTranslation()
  const [openBusinessHoursAccordion, setOpenBusinessHoursAccordion] = useState(false)

  const { openIntercomMessage } = useCustomIntercom()
  const handleOpenIntercom = useCallback(() => {
    openIntercomMessage(true)
  }, [openIntercomMessage])

  const hours = useMemo(() => {
    const result: { hour: string }[] = []

    if (space.working_hours) {
      result.push({
        hour: t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.BUSINESS_HOURS', {
          day: t('COMMON.DAY.TODAY'),
          openAt: space.working_hours.open_at,
          closeAt: space.working_hours.close_at,
        }),
      })
    }

    space.business_hours
      .slice()
      .sort((a, b) => a.day_of_week - b.day_of_week)
      .forEach((bh) => {
        result.push({
          hour: t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.BUSINESS_HOURS', {
            day: t(DAY_TRANSLATION_KEYS[bh.day_of_week]),
            openAt: bh.open_at,
            closeAt: bh.close_at,
          }),
        })
      })

    if (space.holiday_business_hours?.open_at && space.holiday_business_hours?.close_at) {
      result.push({
        hour: t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.BUSINESS_HOURS', {
          day: t('COMMON.DAY.HOLIDAY'),
          openAt: space.holiday_business_hours.open_at,
          closeAt: space.holiday_business_hours.close_at,
        }),
      })
    }

    return result
  }, [space.working_hours, space.business_hours, space.holiday_business_hours, t])

  return (
    <section className={space_styles.storageInformationSection}>
      <div className={space_styles.storageInformationSectionHeading}>
        <h2 className={space_styles.storageInformationSectionHeadingTitle}>
          {t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.TITLE')}
        </h2>
        {showReportButton && (
          <button className={space_styles.report} onClick={handleOpenIntercom}>
            <img src="/warning_icon_blue.svg" alt="" height="13px" width="13px" />
            {t('SPACE_DETAIL.SECTION.BASIC_INFORMATION.REPORT_A_PROBLEM')}
          </button>
        )}
      </div>
      <div className={space_styles.sectionContents}>
        <div className={space_styles.businessHoursSection}>
          <div className={space_styles.businessHoursSectionLabel}>
            <img src="/business_hours_icon.svg" alt="" height="24px" width="24px" />
            <p className={space_styles.labelName}>{t('COMMON.BUSINESS_HOURS_LABEL')}</p>
          </div>
          <div
            className={space_styles.businessHoursSectionDetail}
            onClick={() => setOpenBusinessHoursAccordion(!openBusinessHoursAccordion)}
          >
            <ul className={openBusinessHoursAccordion ? space_styles.open : space_styles.close}>
              {hours.map((item, index) => (
                <li className={space_styles.item} key={index}>
                  {item.hour}
                </li>
              ))}
            </ul>
            <div className={space_styles.arrow}>
              <span style={{ marginTop: i18n.language === 'ja' ? '15%' : 0 }} />
            </div>
          </div>
        </div>
      </div>
      <div className={space_styles.sectionContents}>
        <div className={space_styles.luggageSection}>
          <div className={space_styles.luggageSectionLabel}>
            <img src="/luggage_icon.svg" alt="" height="24px" width="24px" />
            <p className={space_styles.labelName}>
              {t('SPACE_DETAIL.SECTION.STORAGE_INFORMATION.MAX_LUGGAGE_LABEL')}
            </p>
          </div>
          <div className={space_styles.luggageSectionDetail}>
            {luggageLines.map((line, index) => (
              <p className={space_styles.maxBagSize} key={index}>
                {line}
              </p>
            ))}
          </div>
        </div>
      </div>
      {children}
    </section>
  )
}
